import type { AuthConfigNanoId } from '@composio/types'
import { useEffect, useMemo } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useAtom } from 'jotai'
import { nanoid } from 'nanoid'

import { useToast } from '~/design-system/atom/use-toast'
import {
  getAuthConfigsByNanoid,
  patchAuthConfigsByNanoid,
  postAuthConfigs,
} from '~/generated/client'
import { useToolkitInfo } from '~/ui/screens/appScreen/queries'
import {
  connectorSetupConfigAtom,
  DEFAULT_CONNECTOR_SETUP_CONFIG,
} from './atom'

interface FieldConfig {
  name: string
  displayName: string
  default?: string | null
  type: string
}

interface SetupFormWizardProps {
  appKey: string
  authConfigId?: AuthConfigNanoId
  initAuthSchemeType?: string
  initAuthConfig?: Record<string, unknown>
  afterConnectorBEChange?: (status: {
    authConfigId: AuthConfigNanoId | undefined | null
    status: string
  }) => void
  defaultConnectorId?: string | null
  viaSettingsPage?: boolean
  limitedActions?: string[]
}

/*
  Rewrite this logic, it became very scrappy. With new BE types, move this completely
  Plan around this component and testing around it
*/

export const useSetupFormWizard = ({
  appKey,
  authConfigId,
  initAuthSchemeType,
  initAuthConfig,
  afterConnectorBEChange,
  limitedActions: _limitedActions,
}: SetupFormWizardProps) => {
  const authConfig = useQuery({
    queryKey: ['authConfig', authConfigId],
    queryFn: () => getAuthConfigsByNanoid({ nanoid: authConfigId! }),
    enabled: !!authConfigId,
  })

  const [newFormState, setNewFormState] = useAtom(connectorSetupConfigAtom)
  const { isUpdating } = newFormState
  const { appInfo, isAppInfoLoading, isError } = useToolkitInfo(appKey)

  // Derived state
  const authConfigSchemes = useMemo(
    () => appInfo?.auth_config_details?.map((schema) => schema.mode) ?? [],
    [appInfo],
  )

  const isExistingConnector = !!authConfigId

  const isManagedComposioAuthAvailable = useMemo(() => {
    const selectedScheme = newFormState.selectedAuthScheme
    const managedAuthSchemes = appInfo?.composio_managed_auth_schemes
    return (
      (managedAuthSchemes?.length ?? 0) > 0 &&
      managedAuthSchemes?.includes(selectedScheme ?? '')
    )
  }, [appInfo, newFormState.selectedAuthScheme])

  // Form field configuration
  const formFieldsForAuthConfigCreation = useMemo(() => {
    const selectedAuthInfo = appInfo?.auth_config_details?.find(
      (config) => config.mode === newFormState.selectedAuthScheme,
    )

    const { required = [], optional = [] } =
      selectedAuthInfo?.fields?.auth_config_creation ?? {}

    const mapFieldsWithRequired = (
      fields: FieldConfig[],
      isRequired: boolean,
    ) =>
      fields.map((field) => ({
        ...field,
        required: isRequired,
        value: field.default ?? undefined,
      }))

    const fields = [...required, ...optional]

    const isComposioAuthAvailable =
      appInfo?.composio_managed_auth_schemes?.includes(
        newFormState.selectedAuthScheme ?? '',
      ) ?? false
    // Update form state with field configurations
    setNewFormState((prev) => ({
      ...prev,
      connectorConfiguration: fields.reduce((acc, field) => {
        const isRequired = required.some(
          (reqField) => reqField.name === field.name,
        )
        return {
          ...acc,
          [field.name]: {
            value: initAuthConfig?.[field.name] ?? field.default ?? undefined,
            required: isRequired,
            expected_from_customer: false,
          },
        }
      }, {}),
      usingComposioAuth: isComposioAuthAvailable,
    }))

    return [
      ...mapFieldsWithRequired(required, true),
      ...mapFieldsWithRequired(optional, false),
    ]
  }, [
    appInfo,
    newFormState.selectedAuthScheme,
    setNewFormState,
    initAuthConfig,
  ])

  // Run this only once as mounting
  useEffect(() => {
    if (!appInfo) return

    const firstAuthScheme = appInfo.auth_config_details?.[0]?.mode ?? null
    const isComposioAuthEnabled =
      (appInfo?.composio_managed_auth_schemes?.length ?? 0) > 0 &&
      !isExistingConnector &&
      firstAuthScheme !== 'CUSTOM'

    setNewFormState((prev) => ({
      ...prev,
      name: isExistingConnector ? prev.name : `${appKey}_${nanoid(4)}`,
      selectedAuthScheme: initAuthSchemeType ?? firstAuthScheme,
      usingComposioAuth: isExistingConnector
        ? (authConfig.data?.is_composio_managed ?? false)
        : (isComposioAuthEnabled ?? false),
      limitedActions: _limitedActions ?? prev.limitedActions,
      connectorConfiguration: initAuthConfig
        ? {
            ...prev.connectorConfiguration,
            ...formFieldsForAuthConfigCreation.reduce(
              (acc, field) => ({
                ...acc,
                [field.name]: {
                  value:
                    initAuthConfig[field.name] ?? field.default ?? undefined,
                  required: field.required,
                  expected_from_customer: false,
                },
              }),
              {},
            ),
          }
        : prev.connectorConfiguration,
    }))
  }, [appInfo, authConfig.data])

  const { toast } = useToast()
  const { mutate } = useMutation({
    mutationFn: (credentials: Record<string, string | number>) => {
      console.log('from mutation', { credentials })
      if (!credentials) throw new Error('No credentials provided')
      return postAuthConfigs({
        requestBody: {
          toolkit: { slug: appKey },
          auth_config: newFormState.usingComposioAuth
            ? {
                type: 'use_composio_managed_auth',
                name: newFormState.name,
                credentials: credentials,
                restrict_to_following_tools: newFormState.limitedActions ?? [],
              }
            : newFormState.selectedAuthScheme
              ? {
                  type: 'use_custom_auth',
                  name: newFormState.name,
                  authScheme: newFormState.selectedAuthScheme as
                    | 'OAUTH2'
                    | 'OAUTH1'
                    | 'API_KEY'
                    | 'BASIC'
                    | 'BEARER_TOKEN'
                    | 'GOOGLE_SERVICE_ACCOUNT'
                    | 'NO_AUTH'
                    | 'BASIC_WITH_JWT'
                    | 'COMPOSIO_LINK'
                    | 'CALCOM_AUTH',
                  credentials: credentials,
                  restrict_to_following_tools:
                    newFormState.limitedActions ?? [],
                }
              : undefined,
        },
      })
    },
    onSuccess: (data) => {
      const authConfigId = data.auth_config?.id
      afterConnectorBEChange?.({
        authConfigId: authConfigId,
        status: 'success',
      })
      toast({
        title: 'Success',
        description: 'Auth config created successfully',
        variant: 'success',
      })
    },
    onError: (error) => {
      console.error(error)
      toast({
        title: 'Error',
        description: 'Error creating auth config',
        variant: 'destructive',
      })
    },
  })

  const { mutate: mutateExistingConnector } = useMutation({
    mutationFn: ({
      id,
      credentials,
    }: {
      id: AuthConfigNanoId
      credentials: Record<string, unknown>
    }) => {
      return patchAuthConfigsByNanoid({
        nanoid: id,
        requestBody:
          authConfig?.data?.type === 'custom'
            ? {
                type: 'custom',
                credentials,
                restrict_to_following_tools: newFormState.limitedActions ?? [],
              }
            : {
                type: 'default',
                scopes: credentials.scopes as string,
                restrict_to_following_tools: newFormState.limitedActions ?? [],
              },
      })
    },
    onSuccess: () => {
      afterConnectorBEChange?.({
        authConfigId: authConfigId,
        status: 'success',
      })
      toast({
        title: 'Success',
        description: 'Auth config updated successfully',
        variant: 'success',
      })
    },
    onError: (error: unknown) => {
      console.error(error)
      toast({
        title: 'Error',
        description: 'Error creating auth config',
        variant: 'destructive',
      })
    },
  })

  const updateInBackend = () => {
    let hasError = false
    const newFormValues = Object.entries(
      newFormState.connectorConfiguration,
    ).reduce(
      (acc, [key, field]) => {
        if (
          field.required &&
          !field.value &&
          !field.expected_from_customer &&
          !newFormState.usingComposioAuth
        ) {
          acc[key] = { ...field, errors: ['This field is required'] }
          hasError = true
        } else {
          acc[key] = field
        }
        return acc
      },
      { ...newFormState.connectorConfiguration },
    )

    setNewFormState((prev) => ({
      ...prev,
      connectorConfiguration: newFormValues,
    }))

    if (hasError) {
      toast({
        title: 'Missing required fields',
        description: 'Please fill in all required fields',
        variant: 'destructive',
      })
      return
    }

    const credentials = Object.entries(newFormValues).reduce(
      (acc, [key, field]) => ({
        ...acc,
        [key]: field.value as string | number,
      }),
      {} as Record<string, string | number>,
    )

    if (!isExistingConnector) {
      mutate(credentials)
    } else {
      mutateExistingConnector({
        id: authConfigId,
        credentials: credentials,
      })
    }
  }

  useEffect(() => {
    return () => {
      setNewFormState(DEFAULT_CONNECTOR_SETUP_CONFIG)
    }
  }, [])

  return {
    connectorData: appInfo,
    updateInBackend,
    setupConfig: {
      composioAuth: {
        value: newFormState.usingComposioAuth,
        setValue: (usingComposioAuth: boolean) =>
          setNewFormState((prev) => ({ ...prev, usingComposioAuth })),
      },
      authSchemeType: {
        value: newFormState.selectedAuthScheme,
        setValue: (authSchemeType: string) =>
          setNewFormState((prev) => ({
            ...prev,
            selectedAuthScheme: authSchemeType,
          })),
      },
      formState: {
        value: newFormState,
        setValue: setNewFormState,
      },
      limitedActions: {
        value: newFormState.limitedActions,
        setValue: (limitedActions: string[]) =>
          setNewFormState((prev) => ({ ...prev, limitedActions })),
      },
      formFields: formFieldsForAuthConfigCreation,
      schemes: authConfigSchemes,
      isComposioAuthAvailable: isManagedComposioAuthAvailable,
      isExistingConnector: !!authConfigId,
      state: {
        isUpdating,
        isLoading: isAppInfoLoading,
        isError,
      },
    },
  }
}
